import React, { useEffect, useState } from 'react'
import { Row, Col, Modal, List, Button, Form, Input, Space, message, Popconfirm } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import serviceInterface from '../../services/service.interface'
import { constants } from '../../constants'

const PatientDiagnoses = ({ patientId, patient, role, company, customer, hidden }) => {
  const [diagnoses, setDiagnoses] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingDiagnosis, setEditingDiagnosis] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    if (patientId) {
      fetchDiagnoses()
    }
  }, [patientId])

  const fetchDiagnoses = async () => {
    try {
      setLoading(true)
      const response = await serviceInterface
        .PatientService()
        .getPatientDiagnoses(patientId)
      
      if (response.status === 'success') {
        setDiagnoses(response.data?.diagnoses || [])
      } else {
        console.error('Failed to fetch diagnoses:', response.message)
      }
    } catch (error) {
      console.error('Error fetching diagnoses:', error)
    } finally {
      setLoading(false)
    }
  }

  const openModal = (diagnosis = null) => {
    setEditingDiagnosis(diagnosis)
    if (diagnosis) {
      form.setFieldsValue({
        dxCode: diagnosis.dxCode,
        dxDescription: diagnosis.dxDescription,
      })
    } else {
      form.resetFields()
    }
    setModalVisible(true)
  }

  const closeModal = () => {
    setModalVisible(false)
    setEditingDiagnosis(null)
    form.resetFields()
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true)
      let response

      if (editingDiagnosis) {
        // Update existing diagnosis
        response = await serviceInterface
          .PatientService()
          .updatePatientDiagnosis(patientId, {
            ...values,
            originalDxCode: editingDiagnosis.dxCode,
          })
      } else {
        // Add new diagnosis
        response = await serviceInterface
          .PatientService()
          .addSingleDiagnosis(patientId, values)
      }

      if (response.status === 'success') {
        message.success(
          editingDiagnosis ? 'Diagnosis updated successfully' : 'Diagnosis added successfully'
        )
        setDiagnoses(response.data?.diagnoses || [])
        
        // Reset form for adding more diagnoses, but don't close modal
        if (!editingDiagnosis) {
          form.resetFields()
        } else {
          // For edits, clear the editing state to return to add mode
          setEditingDiagnosis(null)
          form.resetFields()
        }
      } else {
        message.error(response.message || 'Operation failed')
      }
    } catch (error) {
      console.error('Error saving diagnosis:', error)
      message.error('An error occurred while saving the diagnosis')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (dxCode) => {
    try {
      setLoading(true)
      const response = await serviceInterface
        .PatientService()
        .deletePatientDiagnosis(patientId, dxCode)

      if (response.status === 'success') {
        message.success('Diagnosis deleted successfully')
        setDiagnoses(response.data?.diagnoses || [])
      } else {
        message.error(response.message || 'Delete failed')
      }
    } catch (error) {
      console.error('Error deleting diagnosis:', error)
      message.error('An error occurred while deleting the diagnosis')
    } finally {
      setLoading(false)
    }
  }

  const canEdit = () => {
    // Same permissions as those who can access the patient page
    return patient?.active && (
      role == constants.ROLE.ADMIN ||           // 1 - CUSTOMER_ADMIN
      role == constants.ROLE.COMPANY_ADMIN ||  // 21 - COMPANY_ADMIN  
      role == constants.ROLE.COMPANY_HEALTHCOACH || // 22 - COMPANY_HEALTHCOACH
      role == constants.ROLE.PHYSIO ||         // 2 - CUSTOMER_PHYSIO
      role == constants.ROLE.MEDICAL_ASSOCIATE || // 4 - CUSTOMER_MEDASO
      role == constants.ROLE.SURGEON          // 3 - CUSTOMER_SURGEON
    )
  }

  return (
    <>
      <Row hidden={hidden} className='infoBody'>
        <Col style={{ width: '100%' }}>
          <Row justify='end'>
            <Col className='infoTitle'>DIAGNOSES</Col>
            <Col className='linkTitle' align='right'>
              {canEdit() && (
                <a onClick={() => openModal()} className={!patient?.active ? 'disable-link' : ''}>
                  <u>{diagnoses.length > 0 ? 'EDIT' : 'ADD'}</u>
                </a>
              )}
            </Col>
          </Row>
          
          {diagnoses.length > 0 ? (
            diagnoses.map((diagnosis, index) => (
              <Row key={index} style={{ marginTop: '4px' }}>
                <Col span={24}>
                  <strong>{diagnosis.dxCode}</strong>: {diagnosis.dxDescription}
                </Col>
              </Row>
            ))
          ) : (
            <Row>
              <Col span={24} style={{ color: '#999', fontStyle: 'italic' }}>
                No diagnoses recorded
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {/* Diagnoses Edit Modal */}
      <Modal
        title={editingDiagnosis ? 'Edit Diagnosis' : 'Manage Diagnoses'}
        visible={modalVisible}
        onCancel={closeModal}
        footer={[
          <Button key='cancel' onClick={closeModal}>
            Cancel
          </Button>,
          <Button key='close' type='primary' onClick={closeModal}>
            Close
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        <Form form={form} onFinish={handleSubmit} layout='vertical' style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label='Diagnosis Code'
                name='dxCode'
                rules={[
                  { required: true, message: 'Please enter diagnosis code' },
                  { max: 20, message: 'Code must be 20 characters or less' },
                ]}
              >
                <Input placeholder='e.g., M16.11' />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label='Diagnosis Description'
                name='dxDescription'
                rules={[
                  { required: true, message: 'Please enter diagnosis description' },
                  { max: 500, message: 'Description must be 500 characters or less' },
                ]}
              >
                <Input placeholder='e.g., Unilateral primary osteoarthritis, right hip' />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type='primary' htmlType='submit' loading={loading}>
              {editingDiagnosis ? 'Update' : 'Add'} Diagnosis
            </Button>
          </Form.Item>
        </Form>

        <div>
          <h4>Current Diagnoses</h4>
          <List
            dataSource={diagnoses}
            renderItem={(diagnosis) => (
              <List.Item
                actions={[
                  <Button
                    type='link'
                    icon={<EditOutlined />}
                    onClick={() => openModal(diagnosis)}
                  >
                    Edit
                  </Button>,
                  <Popconfirm
                    title='Are you sure you want to delete this diagnosis?'
                    onConfirm={() => handleDelete(diagnosis.dxCode)}
                    okText='Yes'
                    cancelText='No'
                  >
                    <Button type='link' danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={<strong>{diagnosis.dxCode}</strong>}
                  description={
                    <div>
                      <div>{diagnosis.dxDescription}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Last modified: {new Date(diagnosis.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
            locale={{ emptyText: 'No diagnoses recorded' }}
          />
        </div>
      </Modal>
    </>
  )
}

PatientDiagnoses.propTypes = {
  patientId: PropTypes.string.isRequired,
  patient: PropTypes.object.isRequired,
  role: PropTypes.number.isRequired,
  company: PropTypes.object,
  customer: PropTypes.object,
  hidden: PropTypes.bool,
}

const mapStateToProps = (state) => ({
  patient: state.patient.patient,
  role: state.profile.user.role,
  company: state.profile.company,
  customer: state.profile.customer,
})

export default connect(mapStateToProps)(PatientDiagnoses)